/**
 * @file Horizon d'ouverture du calendrier côté client (fenêtre glissante ou date butoir fixe).
 * @module packages.common.components.table-reservation.utils.bookingHorizon
 *
 * Miroir client de `backend/.../utils/bookingHorizonHelper.js` : le serveur reste
 * l'autorité (validation à la création), le client ne fait que griser les jours
 * hors horizon et adapter les libellés.
 *
 * En mode 'fixed_date', `openUntilDate` est un jour calendaire 'YYYY-MM-DD'
 * (fuseau établissement). Il est comparé aux jours du calendrier en local
 * navigateur — même convention que les exceptions sérialisées en 'YYYY-MM-DD'.
 *
 * En mode glissant ancré 'service_start' (`horizonAnchor`), chaque service d'un
 * jour ouvre `advanceBookingDays` jours avant sa propre heure de début : le
 * grisage et l'infobulle utilisent le planning hebdomadaire (`serviceSchedule`)
 * en heure locale navigateur ; le serveur (fuseau établissement) reste l'autorité.
 */

import {addDays, addMinutes, startOfDay, parseISO, isValid} from 'date-fns';
import {resolveExceptionOpenServices} from 'packages/common/hooks/useServiceHours';

export const HORIZON_ROLLING = 'rolling';
export const HORIZON_FIXED_DATE = 'fixed_date';

export const HORIZON_ANCHOR_MIDNIGHT = 'midnight';
export const HORIZON_ANCHOR_SERVICE_START = 'service_start';

/** Fenêtre glissante par défaut — doit rester alignée sur le backend (`bookingHorizonHelper`). */
export const DEFAULT_ADVANCE_BOOKING_DAYS = 90;

const DAY_STRING_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Indique si les contraintes décrivent un horizon à date butoir fixe exploitable.
 *
 * @param {Object} bookingConstraints - Contraintes (horizonMode, openUntilDate, advanceBookingDays)
 * @return {boolean} true si le mode 'fixed_date' est actif avec une date valide
 */
export function isFixedHorizon(bookingConstraints) {
  return bookingConstraints?.horizonMode === HORIZON_FIXED_DATE &&
    typeof bookingConstraints.openUntilDate === 'string' &&
    DAY_STRING_REGEX.test(bookingConstraints.openUntilDate);
}

/**
 * Retourne le dernier jour réservable (minuit local), selon le mode d'horizon.
 *
 * Un jour du calendrier est réservable tant que `startOfDay(jour) <= résultat` :
 * en mode fixe le jour butoir est donc inclus, en mode glissant le comportement
 * historique (`aujourd'hui + advanceBookingDays`) est conservé.
 *
 * @param {Object} bookingConstraints - Contraintes (horizonMode, openUntilDate, advanceBookingDays)
 * @param {Date} [now] - Instant de référence (défaut : maintenant)
 * @return {Date} Dernier jour réservable, à minuit local
 */
export function getMaximumBookingDate(bookingConstraints, now = new Date()) {
  if (isFixedHorizon(bookingConstraints)) {
    const openUntil = parseISO(bookingConstraints.openUntilDate);
    if (isValid(openUntil)) {
      return startOfDay(openUntil);
    }
  }
  return startOfDay(addDays(now, bookingConstraints?.advanceBookingDays || DEFAULT_ADVANCE_BOOKING_DAYS));
}

/**
 * Indique si l'horizon glissant est ancré sur l'heure de début des services.
 *
 * @param {Object} bookingConstraints - Contraintes (horizonMode, horizonAnchor)
 * @return {boolean} true si chaque service ouvre à sa propre heure de début
 */
export function isServiceStartAnchor(bookingConstraints) {
  return !isFixedHorizon(bookingConstraints) &&
    bookingConstraints?.horizonAnchor === HORIZON_ANCHOR_SERVICE_START;
}

/** Clés du planning hebdomadaire, indexées par `Date.getDay()` (0 = dimanche). */
const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Convertit une heure 'HH:MM' en minutes depuis minuit, ou null si invalide.
 *
 * @param {string} start - Heure de début au format 'HH:MM'
 * @return {number|null} Minutes depuis minuit
 */
function parseStartMinutes(start) {
  if (typeof start !== 'string' || !/^([01]?\d|2[0-3]):[0-5]\d$/.test(start)) return null;
  const [hours, minutes] = start.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Calcule l'instant (heure locale) auquel un jour du calendrier s'ouvre à la
 * réservation, en horizon glissant ancré sur l'heure du service.
 *
 * L'heure retenue est celle du service sélectionné s'il existe ce jour-là,
 * sinon celle du premier service de la journée (ouverture la plus précoce).
 * L'exception du jour est résolue contre le planning hebdomadaire par
 * `resolveExceptionOpenServices` (source unique des deux modèles d'exception) :
 * en partiel, un moment fermé disparaît et un moment absent garde son horaire
 * normal ; en replace, seuls les moments listés existent. Même cascade que
 * `resolveServiceConfig` côté serveur.
 * Retourne null hors ancrage service ou si le planning du jour est inexploitable :
 * l'appelant retombe alors sur le comportement minuit (`getMaximumBookingDate`).
 *
 * @param {Object} bookingConstraints - Contraintes (horizonMode, horizonAnchor, advanceBookingDays)
 * @param {Date} day - Jour du calendrier
 * @param {Object} [options]
 * @param {Object} [options.serviceSchedule] - Planning hebdomadaire des services
 * @param {string} [options.selectedService] - Nom du service sélectionné (ex. "midi")
 * @param {Object} [options.exception] - Exception applicable au jour (services, servicesMode, isClosed)
 * @return {Date|null} Instant d'ouverture local, ou null (fallback minuit)
 */
export function getDayOpeningInstant(bookingConstraints, day, {serviceSchedule, selectedService, exception} = {}) {
  if (!isServiceStartAnchor(bookingConstraints)) return null;

  const weeklyDayServices = serviceSchedule?.[WEEKDAY_KEYS[day.getDay()]] || [];
  const daySchedule = resolveExceptionOpenServices(exception, weeklyDayServices) ?? weeklyDayServices;
  const activeStarts = daySchedule
      .filter((service) => service.active !== false)
      .map((service) => ({name: service.name, minutes: parseStartMinutes(service.start)}))
      .filter((service) => service.minutes !== null);
  if (activeStarts.length === 0) return null;

  const selected = selectedService ? activeStarts.find((service) => service.name === selectedService) : null;
  const startMinutes = selected ? selected.minutes : Math.min(...activeStarts.map((service) => service.minutes));

  const advanceBookingDays = bookingConstraints?.advanceBookingDays || DEFAULT_ADVANCE_BOOKING_DAYS;
  return addMinutes(addDays(startOfDay(day), -advanceBookingDays), startMinutes);
}

/**
 * Indique si un jour du calendrier est déjà ouvert à la réservation.
 *
 * Source unique de la règle « ce jour est-il dans la fenêtre de réservation » :
 * le calendrier s'en sert pour griser les jours pas encore ouverts, le bandeau
 * « Prochaines disponibilités » pour ne pas compter ces jours comme des places.
 * Ancre service : ouvert dès que l'instant d'ouverture est passé. Ancre minuit
 * ou butoir fixe : ouvert tant que le jour ne dépasse pas le dernier jour réservable.
 *
 * @param {Object} bookingConstraints - Contraintes (horizonMode, horizonAnchor, advanceBookingDays, openUntilDate)
 * @param {Date} day - Jour du calendrier
 * @param {Object} [options] - Planning et exception du jour (voir `getDayOpeningInstant`)
 * @param {Date} [now] - Instant de référence (défaut : maintenant)
 * @return {boolean} true si le client peut réserver ce jour dès maintenant
 */
export function isDayOpenForBooking(bookingConstraints, day, options = {}, now = new Date()) {
  const openingInstant = getDayOpeningInstant(bookingConstraints, day, options);
  if (openingInstant) return now >= openingInstant;
  return startOfDay(day) <= getMaximumBookingDate(bookingConstraints, now);
}

/**
 * Indique si le calendrier est entièrement fermé (mode fixe avec butoir déjà passé).
 *
 * @param {Object} bookingConstraints - Contraintes (horizonMode, openUntilDate)
 * @param {Date} [now] - Instant de référence (défaut : maintenant)
 * @return {boolean} true si plus aucun jour (aujourd'hui inclus) n'est réservable
 */
export function isBookingClosed(bookingConstraints, now = new Date()) {
  return isFixedHorizon(bookingConstraints) &&
    getMaximumBookingDate(bookingConstraints, now) < startOfDay(now);
}
